import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { errMsg, saveAuth, isAuthed, getUser } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { UserRound, Mail, Lock, Chrome, ArrowRight } from "lucide-react";

export default function GuestAuth() {
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [mode, setMode] = useState(params.get("mode") || "login");
  const returnUrl = params.get("return") || "";
  // "launch=1" or "?return=/p/..." triggers the branded-only booking flow:
  // hide the main Navbar, show the launch-discount banner, and redirect
  // back to the chosen room after a successful register / login.
  const brandedFlow = params.get("launch") === "1" || !!returnUrl;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // If already authenticated as guest, skip (unless we're mid-booking flow —
  // in that case forward directly to the return URL so we don't strand them
  // on their dashboard when they meant to finish booking).
  useEffect(() => {
    if (isAuthed()) {
      const u = getUser();
      if (u?.role === "guest") {
        if (returnUrl) nav(returnUrl, { replace: true });
        else nav("/guest/dashboard", { replace: true });
      }
    }
  }, [nav, returnUrl]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const r = await api.post("/guest/auth/register", { email, password, name });
        saveAuth(r.data.token, r.data.user);
        const promoCode = r.data.launch_promo?.code || "";
        toast.success(IT ? "Benvenuta/o! Account creato." : "Welcome! Account created.");
        // Append the promo code to the return URL so the room dialog can
        // auto-apply the 5% without a second network hop.
        let dest;
        if (returnUrl) {
          const sep = returnUrl.includes("?") ? "&" : "?";
          dest = `/guest/welcome?return=${encodeURIComponent(returnUrl + (promoCode ? `${sep}promo=${promoCode}` : ""))}${promoCode ? `&promo=${promoCode}` : ""}`;
        } else {
          dest = `/guest/welcome${promoCode ? `?promo=${promoCode}` : ""}`;
        }
        nav(dest, { replace: true });
      } else {
        const r = await api.post("/auth/login", { email, password });
        if (r.data.user?.role !== "guest") {
          toast.error(IT ? "Questo account non è un account ospite. Usa il login principale." : "This account is not a guest account.");
          setLoading(false); return;
        }
        saveAuth(r.data.token, r.data.user);
        // Existing guests skip the welcome screen — they just return to the
        // room (Vetrina will auto-fetch their launch promo on mount).
        nav(returnUrl || "/guest/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(errMsg(err, IT ? "Errore di autenticazione" : "Auth error"));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Emergent Google auth loses the ?return= param through the callback, so we stash it
    // in sessionStorage — GuestAuthCallback reads it back on the way home.
    if (returnUrl) sessionStorage.setItem("hh_post_login_return", returnUrl);
    const redirectUrl = window.location.origin + "/guest/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [magicSent, setMagicSent] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);

  const magicLink = async () => {
    if (!email) {
      toast.error(IT ? "Inserisci prima la tua email qui sotto." : "Enter your email below first.");
      return;
    }
    setMagicLoading(true);
    try {
      await api.post("/guest/auth/magic-link/request", { email });
      setMagicSent(true);
      toast.success(IT ? "Link inviato! Controlla la tua email." : "Link sent! Check your inbox.");
    } catch (e) {
      toast.error(errMsg(e, IT ? "Invio non riuscito" : "Send failed"));
    } finally { setMagicLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background" style={brandedFlow ? { background: "radial-gradient(ellipse at top, #f7edd4 0%, #eddcb0 100%)", color: "#3a2a17" } : undefined}>
      {!brandedFlow && <Navbar />}
      {brandedFlow && (
        <div className="max-w-md mx-auto px-6 pt-10 flex items-center gap-2 justify-center" data-testid="guest-auth-brand">
          <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center font-display italic text-lg">H</div>
          <span className="font-display italic text-xl" style={{ fontFamily: "'IM Fell English', Georgia, serif" }}>
            HospitalityHub
          </span>
        </div>
      )}
      <div className="max-w-md mx-auto px-6 py-12" data-testid="guest-auth-page">
        {brandedFlow && (
          <div className="mb-6 border-2 border-primary/50 rounded-md p-4 bg-white/50 backdrop-blur-sm shadow-sm" data-testid="guest-auth-launch-banner">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse"/>
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-semibold">
                {IT ? "Sconto di Lancio HospitalityHub" : "HospitalityHub Launch Discount"}
              </span>
            </div>
            <div className="font-display italic leading-tight" style={{ fontFamily: "'IM Fell English', Georgia, serif", fontSize: "1.5rem" }}>
              {IT ? "Registrati ora, prenota con -5%" : "Register now, book with -5%"}
            </div>
            <p className="text-xs italic mt-1" style={{ color: "#5b3d18" }}>
              {IT
                ? "Il codice sarà applicato in automatico alla camera che stai scegliendo. In omaggio anche la Mappa del Tesoro della città."
                : "The code will be applied automatically to the room you're picking. Plus the city Treasure Map as a bonus."}
            </p>
          </div>
        )}
        <div className="uppercase tracking-[0.2em] text-[10px] font-semibold mb-2" style={brandedFlow ? { color: "#8a5a2b" } : { color: "var(--sage)" }}>
          {IT ? "Area viaggiatori" : "Traveller area"}
        </div>
        <h1 className="font-display text-4xl mb-2" style={brandedFlow ? { fontFamily: "'IM Fell English', Georgia, serif", fontStyle: "italic" } : undefined}>
          {mode === "register"
            ? (IT ? "Crea il tuo account" : "Create your account")
            : (IT ? "Bentornata/o" : "Welcome back")}
        </h1>
        <p className="text-sm mb-8" style={brandedFlow ? { color: "#5b3d18" } : { color: "var(--muted-foreground)" }}>
          {IT
            ? "Con un account ritrovi tutte le tue prenotazioni, salvi le strutture preferite e prenoti più velocemente."
            : "With an account you can find all your bookings, save favorite places and book faster."}
        </p>

        {/* Google */}
        <button
          type="button"
          onClick={googleLogin}
          className="w-full border border-border rounded-lg py-3 px-4 flex items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-all mb-3 lift"
          data-testid="guest-google-btn"
        >
          <Chrome className="w-4 h-4"/>
          <span className="text-sm">{IT ? "Continua con Google" : "Continue with Google"}</span>
        </button>

        <button
          type="button"
          onClick={magicLink}
          disabled={magicLoading || magicSent}
          className="w-full border border-border rounded-lg py-3 px-4 flex items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-all mb-6 lift disabled:opacity-60"
          data-testid="guest-magic-btn"
        >
          <Mail className="w-4 h-4"/>
          <span className="text-sm">
            {magicSent
              ? (IT ? "Link inviato ✓ · controlla l'email" : "Link sent ✓ · check your inbox")
              : magicLoading
                ? (IT ? "Invio in corso…" : "Sending…")
                : (IT ? "Ricevi link di accesso via email" : "Email me a login link")}
          </span>
        </button>

        <div className="relative mb-6">
          <div className="border-t border-border"/>
          <div className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-background px-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            {IT ? "oppure con email" : "or with email"}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><UserRound className="w-3 h-3"/>{IT ? "Nome" : "Name"}</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={IT ? "Il tuo nome" : "Your name"} data-testid="guest-name"/>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3"/>Email</Label>
            <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="guest-email"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3"/>Password</Label>
            <Input required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={IT ? "Almeno 8 caratteri" : "At least 8 chars"} data-testid="guest-password"/>
          </div>
          <Button type="submit" disabled={loading} className="w-full lift" data-testid="guest-submit">
            {loading ? "…" : (mode === "register" ? (IT ? "Crea account" : "Create account") : (IT ? "Accedi" : "Log in"))}
            <ArrowRight className="w-4 h-4 ml-1"/>
          </Button>
        </form>

        <div className="text-center text-sm text-muted-foreground mt-6">
          {mode === "register" ? (
            <>
              {IT ? "Hai già un account?" : "Already have an account?"}{" "}
              <button type="button" onClick={() => setMode("login")} className="text-primary hover:underline" data-testid="guest-switch-login">
                {IT ? "Accedi" : "Log in"}
              </button>
            </>
          ) : (
            <>
              {IT ? "Non hai un account?" : "No account yet?"}{" "}
              <button type="button" onClick={() => setMode("register")} className="text-primary hover:underline" data-testid="guest-switch-register">
                {IT ? "Registrati" : "Sign up"}
              </button>
            </>
          )}
        </div>

        <div className="text-center text-[11px] text-muted-foreground mt-8">
          {IT ? "Gestisci una struttura?" : "Are you a host?"}{" "}
          <Link to="/auth?mode=login" className="text-primary hover:underline">
            {IT ? "Vai al login struttura →" : "Host login →"}
          </Link>
        </div>
      </div>
    </div>
  );
}
